<template>
    <!-- User strip -->
    <div
        v-if="user"
        ref="userStripRef"
        class="zs-sidebar-user"
        :class="{ 'is-impersonating': isImpersonating, 'is-impersonating-user': isImpersonatingUser }"
        @click="openUserMenu"
        @contextmenu.prevent="hasAdditionalTenants && openTenantSwitcher()"
    >
        <span v-if="isImpersonating" class="zs-imp-tag">
            <i class="fa-solid fa-user-secret" />
            Impersonating
        </span>
        <Avatar :contact="user" size="xs" extra-classes="zs-sidebar-user__avatar" />
        <div class="zs-sidebar-user__col">
            <div class="zs-sidebar-user__name">{{ user.firstName }} {{ user.lastName }}</div>
            <div class="zs-sidebar-user__tenant">{{ tenant?.name }}</div>
        </div>
        <i v-if="!canExitImpersonation" class="fa-solid fa-chevron-up zs-sidebar-user__trail" />
        <i v-else class="fa-solid fa-xmark zs-sidebar-user__trail" @click.stop="stopImpersonating" />
    </div>
</template>

<script lang="ts" setup>
import { type ContextMenuItem, presentOverlay, showContextMenu } from '@zyno-io/vue-foundation';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';

import { GlobalState } from '@/global';
import TenantSwitcherModal from '@/modules/auth/exports/m-tenant-switcher.vue';
import { canOpenSecurity } from '@/modules/auth/exports/security-tabs';
import { canAccessMultipleTenants, isTenantPinned } from '@/modules/auth/exports/tenant-store';
import { AuthLoginApi } from '@/openapi-clients-generated/auth';
import Avatar from '@/shared/components/avatar.vue';
import { withWait } from '@/shared/helpers/request.helpers';
import { Auth } from '@/shared/services/auth.service';
import { Logger } from '@/shared/services/logger';
import { THEME_LABELS, THEME_OPTIONS, setThemePreference, themePreference } from '@/shared/services/theme.service';

const router = useRouter();

const user = computed(() => GlobalState.user.value);
const tenant = computed(() => GlobalState.tenant.value);
const isImpersonating = computed(() => !!Auth.impersonatorUserId.value || !!Auth.impersonatorTenantId.value);
// Impersonating a user gets its own color. It wins when both are set: exit clears the user first.
const isImpersonatingUser = computed(() => !!Auth.impersonatorUserId.value);
// User impersonation can always be exited. Tenant impersonation can't be exited where the tenant is
// pinned (ZynoWorkstation, or the mobile app's own masquerade) — keep the normal user-menu chevron instead.
const canExitImpersonation = computed(() => (Auth.impersonatorUserId.value ? true : !!Auth.impersonatorTenantId.value && !isTenantPinned.value));

// ── User strip ───────────────────────────────────────────────────────────────
const userStripRef = ref<HTMLElement>();

// Nothing to switch to where the tenant is pinned (ZynoWorkstation, the mobile app).
const hasAdditionalTenants = computed(() => !isTenantPinned.value && canAccessMultipleTenants.value);

function openUserMenu(event: MouseEvent) {
    const items: (ContextMenuItem | '-')[] = [];
    // Password, fingerprints and encryption passkeys all live behind one entry now.
    if (canOpenSecurity.value) items.push({ title: 'Security', handler: openSecurity }, '-');
    if (hasAdditionalTenants.value) items.push({ title: 'Switch Tenant', handler: openTenantSwitcher });
    // A submenu of the three states rather than one cycling entry: cycling makes you read the label
    // to find out where you are and click up to twice more to get where you want, and it never shows
    // you the options. The check marks the active one — 'Auto (System)' included, which a resolved
    // light/dark label alone would hide.
    items.push({
        title: 'Appearance',
        // U+2007 FIGURE SPACE pads the unticked rows. Titles render as HTML, so an ordinary run of
        // spaces would collapse and left-align them against the ticked one; a figure space is
        // fixed-width and survives.
        items: THEME_OPTIONS.map(option => ({
            title: `${themePreference.value === option ? '✓ ' : '  '}${THEME_LABELS[option]}`,
            handler: () => setThemePreference(option)
        }))
    });
    items.push({ title: 'Logout', handler: logout });
    showContextMenu(event, { items });
}

function openTenantSwitcher() {
    presentOverlay(TenantSwitcherModal, {});
}

async function openSecurity() {
    // Loaded on demand: the sidebar ships in the app shell, and the identity-crypto
    // package has no business in that chunk.
    const { default: MSecurity } = await import('@/modules/auth/exports/m-security.vue');
    presentOverlay(MSecurity, {});
}

async function logout() {
    await withWait('Logging out...', async () => {
        try {
            await AuthLoginApi.postLoginLogout();
        } catch (err) {
            Logger.createScoped('Logout').error('Error performing API logout', err);
        } finally {
            Auth.clearBrowserSession();
        }
    });
}

function stopImpersonating() {
    if (!canExitImpersonation.value) return;
    if (Auth.impersonatorUserId.value) {
        withWait('Deimpersonating...', async () => {
            Auth.clearImpersonatedUser();
            await Auth.loadExistingSession();
        });
    } else if (Auth.impersonatorTenantId.value) {
        withWait('Returning to MSP tenant...', async () => {
            // If impersonation was started from the sysadmin console, return there afterwards
            // (key written by src/modules/sysadmin/helpers/impersonate-tenant.ts).
            const returnPath = sessionStorage.getItem('sysadmin:impersonationReturn');
            sessionStorage.removeItem('sysadmin:impersonationReturn');

            Auth.clearImpersonatedTenant();
            await Auth.reloadExistingSessionForTenantSwitch(router);

            if (returnPath) await router.push(returnPath);
        });
    }
}
</script>
